import { useState, useEffect } from 'react';
import { Download, Share, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export function AddToHomeScreen() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches ||
                       window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Listen for beforeinstallprompt (Android/Chrome)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    // Listen for app installed
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (isIOS) {
      // Show iOS instructions
      setShowIOSInstructions(true);
    } else if (deferredPrompt) {
      // Trigger the install prompt for Android/Chrome
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstalled(true);
      }
      setDeferredPrompt(null);
    } else {
      // Fallback - show generic instructions
      setShowIOSInstructions(true);
    }
  };

  // Don't show if already installed/standalone
  if (isStandalone || isInstalled) {
    return null;
  }

  return (
    <>
      <button
        onClick={handleInstallClick}
        className="flex flex-col items-center gap-1 px-4 py-2 rounded-lg transition-colors text-slate-600 hover:text-[#E63946]"
        data-testid="nav-install"
      >
        <div className="w-6 h-6">
          <Download className="w-6 h-6" />
        </div>
        <span className="text-xs font-semibold">Install</span>
      </button>

      {/* iOS Installation Instructions Dialog */}
      <Dialog open={showIOSInstructions} onOpenChange={setShowIOSInstructions}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center">Add to Home Screen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex justify-center mb-4">
              <img 
                src="/images/weakenders-logo.png" 
                alt="Weakenders" 
                className="w-20 h-20 rounded-xl"
              />
            </div>
            
            {isIOS ? (
              <div className="space-y-3">
                <p className="text-center text-slate-600 text-sm">
                  Install Weakenders on your iPhone for quick access:
                </p>
                <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="bg-[#E63946] text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">1</div>
                    <p className="text-sm">
                      Tap the <Share className="inline w-4 h-4 text-blue-500" /> Share button in Safari
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="bg-[#E63946] text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">2</div>
                    <p className="text-sm">
                      Scroll down and tap "Add to Home Screen"
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="bg-[#E63946] text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">3</div>
                    <p className="text-sm">
                      Tap "Add" in the top right corner
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-center text-slate-600 text-sm">
                  Install Weakenders for quick access:
                </p>
                <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="bg-[#E63946] text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">1</div>
                    <p className="text-sm">
                      Tap the menu icon (three dots) in your browser
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="bg-[#E63946] text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">2</div>
                    <p className="text-sm">
                      Select "Add to Home Screen" or "Install App"
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="bg-[#E63946] text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">3</div>
                    <p className="text-sm">
                      Confirm by tapping "Add"
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <Button 
              onClick={() => setShowIOSInstructions(false)}
              className="w-full bg-[#E63946] hover:bg-[#d32f3d]"
            >
              Got it!
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default AddToHomeScreen;
